import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../auth/AuthContext";
import { useLicense } from "../../licensing/LicenseContext";
import { useSearch } from "./SearchContext";
import { getFolders } from "../../api/adminApi";
import { readUseOpenAIFlag, getApiBase } from "../../utils/env";
import { getFlows } from "../../api/flowApi";
import { FlowStore } from "../flow/FlowTypes";
import { fetchBatchDocumentTags } from "../../hooks/useTags";
import "../common/common.css";

import { buildGroups, getDocKey } from "./searchGroupUtils";
import DeliverModal from "../common/DeliverModal";
import { useDocumentViewer } from "../../hooks/useDocumentViewer";
import { useDbCreationWizard } from "../../hooks/useDbCreationWizard";

import { pdfFirstPageToImageUrl } from "../../utils/pdfUtils";
import FormatPickerModal from "./FormatPickerModal";
import FlowDrawer from "./FlowDrawer";
import WizardHero from "./WizardHero";
import { FolderTile, FolderBreadcrumb } from "./FolderChrome";
import LayerSwitcher, { LAYERS } from "../workspace/LayerSwitcher";
import HomeDashboard from "./HomeDashboard";
import WorkspaceExplorer, { WS_DRAG_TYPE } from "../workspace/WorkspaceExplorer";
import OrgLayerExplorer from "../workspace/OrgLayerExplorer";

/* ─── Helpers ─────────────────────────────────────────────────── */
function getCI(obj, keys) {
  if (!obj) return undefined;
  const map = new Map(Object.keys(obj).map(k => [k.toLowerCase(), k]));
  for (const k of keys) {
    const real = map.get(String(k).toLowerCase());
    if (real !== undefined) return obj[real];
  }
  return undefined;
}

function fmtDate(v) {
  try {
    const dt = typeof v === "string" ? new Date(v) : v;
    if (!isNaN(dt)) return dt.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
    return String(v);
  } catch { return String(v); }
}

// Content-governance row chips (folder + classification). Legal-hold is computed, not on the search
// row, so it only shows in the detail pane. Returns null when there is nothing to show / feature off.
const RP_GOV_CHIP_BASE = {
  display: "inline-flex", alignItems: "center", gap: 3, padding: "1px 7px",
  borderRadius: 999, fontSize: 10.5, fontWeight: 600, lineHeight: 1.5, whiteSpace: "nowrap",
};
const RP_GOV_CHIP = {
  folder: { ...RP_GOV_CHIP_BASE, background: "#eef2ff", color: "#4338ca" },
  klass: { ...RP_GOV_CHIP_BASE, background: "#f5f3ff", color: "#6d28d9" },
};
function govRowChips(row, folderMap, govEnabled) {
  if (!govEnabled) return null;
  const si = getCI(row, ["searchIndexes"]) || {};
  const cls = getCI(si, ["docClassification"]);
  const fidRaw = getCI(row, ["folderId"]);
  const fid = fidRaw != null && fidRaw !== "" ? Number(fidRaw) : null;
  const hasFid = fid != null && !Number.isNaN(fid);
  const folder = hasFid && folderMap ? folderMap.get(fid) : null;
  if (!cls && !hasFid) return null;
  return (
    <>
      {hasFid && (
        <span style={RP_GOV_CHIP.folder} title={folder?.path || `Folder #${fid}`}>
          🗂️ {folder?.name || `#${fid}`}
        </span>
      )}
      {cls && <span style={RP_GOV_CHIP.klass}>🏷️ {cls}</span>}
    </>
  );
}


function getExt(doc) {
  return (getCI(doc, ["fileExtension", "extension", "type"]) || "").toString().toUpperCase() || "DOC";
}

function getDocName(doc) {
  return doc?.originalName || doc?.fileName || doc?.documentName || doc?.name || "Untitled";
}

function getDocDate(doc) {
  return getCI(doc, ["documentDate", "date", "createdAt", "created"]);
}

// 0 means "not counted", not "a document with no pages" — the archive stores 0 wherever a page count was
// never derived (a single-document upload, for one). Reporting it as zero pages would state something false
// about the document, so it reads as unknown.
function getPageCount(doc) {
  const p = Number(doc?.pageCount ?? doc?.pages);
  return isNaN(p) || p <= 0 ? null : p;
}

function getArchiveNo(doc) {
  return getCI(doc, ["ArchiveNo", "archiveno", "archive", "customerNo", "customerNumber"]);
}

function getDbName(doc) {
  return getCI(doc, ["dbName", "DbName", "database", "db"]);
}

function docTagKey(doc, fallback) {
  const db = getDbName(doc) || fallback;
  const an = getArchiveNo(doc);
  return db && an ? `${db}::${an}` : null;
}

function readDocPreviewPref() {
  try {
    const v = localStorage.getItem("pref_show_doc_preview");
    if (!v) return true;
    const s = v.toLowerCase();
    return s === "1" || s === "true" || s === "yes" || s === "on";
  } catch { return true; }
}

function flyToCompareTray(sourceEl) {
  const target = document.querySelector(".compare-tray-badge") || document.querySelector(".results-footer-batch");
  if (!sourceEl || !target) return;
  const srcRect = sourceEl.getBoundingClientRect();
  const tgtRect = target.getBoundingClientRect();
  const ghost = document.createElement("div");
  ghost.className = "compare-fly-ghost";
  ghost.innerHTML = '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="3" width="8" height="18" rx="1"/><rect x="14" y="3" width="8" height="18" rx="1"/></svg>';
  ghost.style.left = srcRect.left + srcRect.width / 2 - 16 + "px";
  ghost.style.top = srcRect.top + srcRect.height / 2 - 16 + "px";
  ghost.style.setProperty("--fly-dx", (tgtRect.left + tgtRect.width / 2 - (srcRect.left + srcRect.width / 2)) + "px");
  ghost.style.setProperty("--fly-dy", (tgtRect.top + tgtRect.height / 2 - (srcRect.top + srcRect.height / 2)) + "px");
  document.body.appendChild(ghost);
  ghost.addEventListener("animationend", () => {
    ghost.remove();
    const badge = document.querySelector(".compare-tray-badge");
    if (badge) {
      badge.classList.add("pulse");
      setTimeout(() => badge.classList.remove("pulse"), 200);
    }
  });
}

export default function ResultsPanel() {
  const { t } = useTranslation();
  const location = useLocation();
  const { token } = useAuth();
  const {
    rows,
    rawResults,
    loading,
    error,
    hasResults,
    runSearch,
    query,
    setQuery,
    clearResults,
    browseFolderId,
    setBrowseFolderId,
    selectDocument,
    selectedDoc,
    setSelectedGroup,
    centerFlow,
    closeFlowInCenter,
    dbs,
    setFlowResults,
    clearFlowResults,
    checkedDocs,
    setCheckedDocs,
    pinForCompare,
    unpinForCompare,
    isPinnedForCompare,
  } = useSearch();

  const CORE_API_BASE = useMemo(() => getApiBase(), []);

  const { formatPicker, viewDocument, viewDocumentWithFormat, closeFormatPicker } =
    useDocumentViewer(token, CORE_API_BASE);

  // Content-governance: the org folder list (row chips + the Explorer landing), loaded once when licensed.
  const { hasFeature, featuresResolved } = useLicense();
  const govEnabled = hasFeature("content-governance");
  // Render off govEnabled (optimistic, so a licensed customer sees no flicker); FETCH off this, which
  // waits for the real licence answer instead of firing a 403 at an unlicensed site on every load.
  const govFetchReady = govEnabled && featuresResolved;
  // Delivery is its own sellable module, gated separately from content-governance.
  const deliveryEnabled = hasFeature("delivery-portal");
  const [deliverOpen, setDeliverOpen] = useState(false);

  // The checked set holds keys, not rows. A DocumentSet grant names ONE document base, so the batch
  // action only offers itself when every selection came from the same one — mixing bases would either
  // silently drop half the selection or need several grants, and neither is what the operator asked for.
  const checkedRows = useMemo(
    () => rows.filter((r) => checkedDocs.has(getDocKey(r))),
    [rows, checkedDocs]);
  const checkedDbNames = useMemo(
    () => new Set(checkedRows.map((r) => r.dbName || r.DbName || r.database).filter(Boolean)),
    [checkedRows]);
  const canDeliverSelection = deliveryEnabled && checkedRows.length > 0 && checkedDbNames.size === 1;
  const [folderList, setFolderList] = useState([]);
  useEffect(() => {
    if (!govFetchReady || !token) { setFolderList([]); return; }
    getFolders(token)
      .then((list) => setFolderList((Array.isArray(list) ? list : []).filter((f) => f && f.id != null && f.isActive)))
      .catch(() => setFolderList([]));
  }, [govFetchReady, token]);
  const folderMap = useMemo(() => {
    const m = new Map();
    folderList.forEach((f) => m.set(Number(f.id), { name: f.name, path: f.path }));
    return m;
  }, [folderList]);

  // Which of the three layers the idle Explorer is looking through. Personal is the default because
  // it is the only layer that already has something in it on somebody's first day — the other two are
  // empty until an administrator has set up a folder plan and a vocabulary.
  const [lens, setLens] = useState(LAYERS.workspace);

  // Folder-browse: the current folder comes from the SHARED search context, so the sidebar folder
  // list and this pane drive the same Explorer view. Listing is delegated to the normal results view.
  const browseCwd = browseFolderId;
  const browseActive = govEnabled && folderList.length > 0 && !(query && String(query).trim());
  const goFolder = useCallback((id) => {
    setBrowseFolderId(id);
    if (id == null) { if (clearResults) clearResults(); }
    else { runSearch(JSON.stringify({ FolderId: { $eq: id } })); }
  }, [setBrowseFolderId, clearResults, runSearch]);
  // A manual (typed) search exits browse mode.
  useEffect(() => { if (query && String(query).trim()) setBrowseFolderId(null); }, [query, setBrowseFolderId]);
  // Search results are draggable into the personal layer — that gesture is the whole point of having
  // a workspace next to search. The payload is the document's identity, never the document: filing is
  // a pointer, so nothing is copied and the archived record is untouched.
  const startResultDrag = useCallback((row) => (e) => {
    const payload = {
      dbName: getDbName(row),
      archiveNo: getArchiveNo(row),
      documentId: row?.id ?? row?.Id ?? null,
    };
    if (!payload.dbName) return;
    try {
      e.dataTransfer.setData(WS_DRAG_TYPE, JSON.stringify(payload));
      e.dataTransfer.setData("text/plain", JSON.stringify(payload));
      e.dataTransfer.effectAllowed = "copy";
    } catch { /* older browsers restrict custom MIME types on drag */ }
  }, []);

  // A lens hands back either a query string or a raw filter plus options (taxonomy terms are a
  // server-side JOIN, not something the filter expression can express).
  const runLensSearch = useCallback((text, opts) => {
    setBrowseFolderId(null);
    runSearch(text, opts);
  }, [runSearch, setBrowseFolderId]);

  // Opening a document from the personal layer puts it in the ordinary detail pane. The workspace row
  // is not a search row, so it is mapped onto the shape the rest of the app already reads — the point
  // of the personal layer is to be a lens over the archive, not a second viewer.
  const openWorkspaceDocument = useCallback((d) => {
    if (!d) return;
    selectDocument({
      // The encrypted "db|id" link, carried through verbatim — this is what View / Download / Compare
      // open. Without it the detail pane can show the document's metadata and nothing else, which is
      // exactly how an uploaded document became unreachable from the folder it was filed in.
      itemId: d.itemId,
      id: d.id,
      dbName: d.dbName,
      archiveNo: d.archiveNo,
      customerNo: d.archiveNo,
      originalName: d.originalFileName || d.fileName,
      fileName: d.fileName || d.originalFileName,
      fileExtension: d.fileExtension,
      pageCount: d.pageCount,
      documentDate: d.documentDate,
      folderId: d.folderId,
    });
  }, [selectDocument]);

  // Close the current results and return to the folder Explorer landing.
  const backToFolders = useCallback(() => {
    setBrowseFolderId(null);
    if (typeof setQuery === "function") setQuery("");
    if (clearResults) clearResults();
  }, [setBrowseFolderId, setQuery, clearResults]);

  const isProd =
    typeof process !== "undefined" && process.env.NODE_ENV === "production";
  const searchParams = new URLSearchParams(location.search || "");
  const wizardFlag =
    !isProd &&
    ["1", "true", "yes"].includes(
      (searchParams.get("wizard") || "").toLowerCase()
    );

  const hasDbs = Array.isArray(dbs) && dbs.length > 0;

  // True while viewing a folder's documents in browse mode — the results view then suppresses its
  // own (DB-centric) breadcrumb and shows the folder name, so the folder breadcrumb is the only one.
  const browseMode = browseActive && !error && hasDbs && !wizardFlag && browseCwd != null;
  const currentFolderName = browseCwd != null ? (folderMap.get(Number(browseCwd))?.name || "") : "";

  const groups = useMemo(() => buildGroups(rows, rawResults), [rows, rawResults]);

  const [flows, setFlows] = useState(() => FlowStore.loadAll());
  useEffect(() => {
    if (!token) return;
    getFlows(token)
      .then((serverFlows) => {
        if (Array.isArray(serverFlows) && serverFlows.length > 0) {
          FlowStore.syncFromServer(serverFlows);
          setFlows(serverFlows);
        }
      })
      .catch(() => {});
  }, [token]);

  const wizard = useDbCreationWizard({ token, dbs, t });

  const [dbCollapsed, setDbCollapsed] = useState(false);
  const [collapsedGroups, setCollapsedGroups] = useState(new Set());
  const [viewMode, setViewMode] = useState("list"); // "list" | "grid"
  const [hoveredRow, setHoveredRow] = useState(null);
  const [hoverPos, setHoverPos] = useState({ x: 0, y: 0 });
  const showDocPreview = useMemo(() => readDocPreviewPref(), []);
  const hoverTimer = useRef(null);
  const lastClickedIdx = useRef(null); // for shift-click range select
  const thumbCache = useRef(new Map()); // link → blob URL cache for thumbnails
  const [thumbState, setThumbState] = useState(null); // null | { loading } | { url }
  const thumbAbort = useRef(null); // AbortController for in-flight thumbnail fetch

  // Tags for all visible documents: Map<"dbName::archiveNo", [{tagId, tagName}]>
  const [docTagsMap, setDocTagsMap] = useState(new Map());
  const tagFetchRef = useRef(0);

  // Fallback DB name from the response or dbs list (rows often don't carry it)
  const fallbackDb = useMemo(() => {
    return (rawResults?.responses?.[0]?.database || rawResults?.responses?.[0]?.dbName)
      || dbs?.[0]?.dbName || null;
  }, [rawResults, dbs]);

  const buildDocRefs = useCallback(() => {
    if (!rows?.length) return [];
    const docs = [];
    for (const row of rows) {
      const db = getDbName(row) || fallbackDb;
      const an = getArchiveNo(row);
      if (db && an) docs.push({ dbName: db, archiveNo: an });
    }
    return docs;
  }, [rows, fallbackDb]);

  useEffect(() => {
    if (!rows?.length || !token) { setDocTagsMap(new Map()); return; }
    const docs = buildDocRefs();
    if (!docs.length) { setDocTagsMap(new Map()); return; }
    const id = ++tagFetchRef.current;
    fetchBatchDocumentTags(token, docs).then((map) => {
      if (tagFetchRef.current === id) setDocTagsMap(map);
    });
  }, [rows, token, buildDocRefs]);

  // Listen for tag updates from the footer bar
  const refreshTags = useCallback(() => {
    if (!rows?.length || !token) return;
    const docs = buildDocRefs();
    if (docs.length) {
      const id = ++tagFetchRef.current;
      fetchBatchDocumentTags(token, docs).then((map) => {
        if (tagFetchRef.current === id) setDocTagsMap(map);
      });
    }
  }, [rows, token, buildDocRefs]);

  useEffect(() => {
    const handler = () => refreshTags();
    window.addEventListener("tags-updated", handler);
    return () => window.removeEventListener("tags-updated", handler);
  }, [refreshTags]);

  const fetchThumb = useCallback(async (row) => {
    // Abort any in-flight thumbnail fetch
    if (thumbAbort.current) thumbAbort.current.abort();

    const link = row?.itemId || row?.ItemId || row?.link || row?.Link;
    if (!link || !token) { setThumbState(null); return; }

    // Check cache first
    if (thumbCache.current.has(link)) {
      setThumbState({ url: thumbCache.current.get(link) });
      return;
    }

    const ac = new AbortController();
    thumbAbort.current = ac;
    setThumbState({ loading: true });
    try {
      const resp = await fetch(`${CORE_API_BASE}/api/Document/ViewDocument`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ requests: [{ requestType: 0, link, outputFormat: "PDF" }] }),
        signal: ac.signal,
      });
      if (!resp.ok) { setThumbState(null); return; }
      const buf = await resp.arrayBuffer();
      const url = await pdfFirstPageToImageUrl(buf, 1);
      thumbCache.current.set(link, url);
      setThumbState({ url });
    } catch (e) {
      if (e.name !== "AbortError") setThumbState(null);
    }
  }, [token, CORE_API_BASE]);

  const onRowHover = useCallback((row, e) => {
    if (!showDocPreview) return;
    const pos = { x: e.clientX - 80, y: e.clientY - 260 };
    clearTimeout(hoverTimer.current);
    hoverTimer.current = setTimeout(() => {
      setHoveredRow(row);
      setHoverPos(pos);
      fetchThumb(row);
    }, 3000);
  }, [showDocPreview, fetchThumb]);

  const onRowLeave = useCallback(() => {
    clearTimeout(hoverTimer.current);
    if (thumbAbort.current) thumbAbort.current.abort();
    setHoveredRow(null);
    setThumbState(null);
  }, []);

  // Reset when new results arrive
  useEffect(() => {
    setCheckedDocs(new Set());
    setDbCollapsed(false);
    setCollapsedGroups(new Set());
    wizard.resetWizard();
    if (typeof setSelectedGroup === "function") setSelectedGroup(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, rawResults, setSelectedGroup]);

  const toggleDateGroup = useCallback((dateLabel) => {
    setCollapsedGroups(prev => {
      const next = new Set(prev);
      if (next.has(dateLabel)) next.delete(dateLabel);
      else next.add(dateLabel);
      return next;
    });
  }, []);

  const toggleCheck = useCallback((docKey) => {
    setCheckedDocs(prev => {
      const next = new Set(prev);
      if (next.has(docKey)) next.delete(docKey);
      else next.add(docKey);
      return next;
    });
  }, []);

  // Group items by date, per database group
  const perGroupDateGroups = useMemo(() => {
    return groups.map(group => {
      const map = new Map();
      for (const row of group.items) {
        const d = getDocDate(row);
        const key = d ? fmtDate(d) : "Unknown Date";
        if (!map.has(key)) map.set(key, []);
        map.get(key).push(row);
      }
      return { name: group.name, count: group.items.length, dateMap: map };
    });
  }, [groups]);

  // Flat ordered list of all doc keys for shift-click range select
  const allKeys = useMemo(() => {
    const keys = [];
    for (const g of perGroupDateGroups) {
      for (const [dateLabel, items] of g.dateMap.entries()) {
        items.forEach((_, idx) => keys.push(`${g.name}:${dateLabel}-${idx}`));
      }
    }
    return keys;
  }, [perGroupDateGroups]);

  const handleGridClick = useCallback((key, row, e) => {
    const isCtrl = e.metaKey || e.ctrlKey;
    const isShift = e.shiftKey;

    if (isCtrl) {
      toggleCheck(key);
      lastClickedIdx.current = allKeys.indexOf(key);
    } else if (isShift && lastClickedIdx.current != null) {
      const curIdx = allKeys.indexOf(key);
      const start = Math.min(lastClickedIdx.current, curIdx);
      const end = Math.max(lastClickedIdx.current, curIdx);
      setCheckedDocs(prev => {
        const next = new Set(prev);
        for (let i = start; i <= end; i++) next.add(allKeys[i]);
        return next;
      });
    } else {
      if (checkedDocs.size > 0) {
        setCheckedDocs(new Set([key]));
      } else {
        selectDocument(row);
      }
      lastClickedIdx.current = allKeys.indexOf(key);
    }
  }, [allKeys, toggleCheck, checkedDocs.size, selectDocument]);

  const totalResults = (rows || []).length;

  // First DB name for display
  const firstDb = useMemo(() => {
    return groups.length > 0 ? groups[0].name : "";
  }, [groups]);

  // ── Wizard callbacks ──
  const handleStartWizard = () => {
    wizard.setWizardStep("create-db-name");
    wizard.setWizardDbError("");
    if (!wizard.wizardMessages.length) {
      wizard.setWizardMessages([{
        id: Date.now().toString() + "-ai-start",
        role: "ai",
        text: t("results.wizardAskDbName", { defaultValue: "What should we call your first database?" }),
      }]);
    }
  };

  const handleConfirmYes = () => {
    wizard.setWizardDbError("");
    wizard.setWizardStep("create-db-desc");
    wizard.setWizardMessages((prev) => [...prev,
      { id: Date.now().toString() + "-user-confirm-btn", role: "user", text: t("results.wizardConfirmYes", { defaultValue: "Looks good" }) },
      { id: Date.now().toString() + "-ai-desc-btn", role: "ai", text: t("results.wizardAskDbDesc", { defaultValue: "Anything you'd like to note about it? (optional)" }) },
    ]);
  };

  const handleConfirmChange = () => {
    wizard.setWizardDbError("");
    wizard.setWizardStep("create-db-name");
    wizard.setWizardMessages((prev) => [...prev,
      { id: Date.now().toString() + "-user-change-btn", role: "user", text: t("results.wizardConfirmChange", { defaultValue: "Change name" }) },
    ]);
  };

  const handleSkipDesc = () => {
    wizard.setWizardDbError("");
    try {
      localStorage.setItem("wizard_db_meta_draft", JSON.stringify({ name: (wizard.wizardDbName || "").trim(), description: null, ts: Date.now() }));
    } catch {}
    wizard.setWizardDbDescription("");
    wizard.setWizardMessages((prev) => [...prev,
      { id: Date.now().toString() + "-user-skip-btn", role: "user", text: t("results.wizardSkipDesc", { defaultValue: "Skip description" }) },
    ]);
  };

  const handleWizardCancel = () => {
    wizard.setWizardStep(null);
    wizard.setWizardDbError("");
    wizard.setWizardDbDescription("");
    wizard.setWizardMessages([]);
  };

  // ── Results content ──
  let resultsContent = null;

  if (loading) {
    resultsContent = (
      <div style={{ textAlign: "center", padding: "60px 20px", color: "#6b7280" }}>
        <div style={{ fontSize: 16, fontWeight: 600 }}>{t("common.searching") || "Searching..."}</div>
      </div>
    );
  } else if (error && !hasResults) {
    resultsContent = (
      <div style={{ color: "#555" }}>
        {t("results.noResultsReview")}{" "}
        <Link to="/system-logs" className="link-btn">{t("system.logs")}</Link>.
      </div>
    );
  } else if ((wizardFlag || !hasDbs) && !hasResults) {
    const aiEnabled = readUseOpenAIFlag();
    resultsContent = (
      <WizardHero
        wizardStep={wizard.wizardStep}
        wizardDbName={wizard.wizardDbName}
        wizardDbError={wizard.wizardDbError}
        wizardMessages={wizard.wizardMessages}
        wizardInput={wizard.wizardInput}
        onWizardInputChange={(e) => wizard.setWizardInput(e.target.value)}
        assistantLoading={wizard.assistantLoading}
        assistantError={wizard.assistantError}
        creatingDb={wizard.creatingDb}
        aiEnabled={aiEnabled}
        wizardFlag={wizardFlag}
        onSubmit={wizard.handleWizardSubmit}
        onConfirmYes={handleConfirmYes}
        onConfirmChange={handleConfirmChange}
        onSkipDesc={handleSkipDesc}
        onCreateNow={wizard.handleCreateFromWizard}
        onCancel={handleWizardCancel}
        onStartWizard={handleStartWizard}
        t={t}
      />
    );
  } else if (!hasResults) {
    resultsContent = (
      <div style={{ textAlign: "center", padding: "80px 20px", color: "#9ca3af" }}>
        <div style={{ marginBottom: 16 }}>
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#c7ccd6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" /></svg>
        </div>
        <div style={{ fontSize: 16, fontWeight: 600, color: "#9ca3af", marginBottom: 8 }}>{t("results.noResultsTitle")}</div>
        <div style={{ fontSize: 13, color: "#b0b5bf" }}>{t("results.noResultsHint")}</div>
      </div>
    );
  } else {
    resultsContent = (
      <>
        {/* Header */}
        <div className="rp-header">
          <div style={{ display: "flex", flexDirection: "column" }}>
            {!browseMode && (
              <div className="rp-breadcrumb">
                <a href="/">{t("nav.home", { defaultValue: "Home" })}</a>
                <span className="rp-breadcrumb-sep">›</span>
                <span>{groups.length > 1 ? t("results.multipleDatabases", { defaultValue: "Multiple databases" }) : (firstDb || t("results.title", { defaultValue: "Results" }))}</span>
              </div>
            )}
            <div className="rp-db-name">
              {browseMode ? currentFolderName : (groups.length > 1 ? t("results.multipleDatabases", { defaultValue: "Multiple databases" }) : (firstDb || "Results"))} <span className="rp-doc-count">&middot; {totalResults} {t("results.documents") || "documents"}</span>
              {checkedDocs.size > 0 && (
                <span className="rp-selection-info">
                  <span className="rp-selection-count">{checkedDocs.size} {t("results.selected", { defaultValue: "selected" })}</span>
                  {canDeliverSelection && (
                    <button
                      className="rp-selection-deliver"
                      onClick={() => setDeliverOpen(true)}
                      title={t("deliverModal.title", { defaultValue: "Deliver documents" })}
                    >
                      {t("detail.deliver", { defaultValue: "Deliver…" })}
                    </button>
                  )}
                  <button className="rp-selection-clear" onClick={() => setCheckedDocs(new Set())}>&times;</button>
                </span>
              )}
            </div>
          </div>
          <div className="rp-header-actions">
            {govEnabled && folderList.length > 0 && (
              <button className="rp-collapse-btn" onClick={backToFolders} title={t("explorer.backToFolders", { defaultValue: "Back to folders" })} style={{ width: "auto", padding: "0 10px", gap: 6, display: "inline-flex", alignItems: "center" }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/></svg>
                <span style={{ fontSize: 12, fontWeight: 600 }}>{t("explorer.home", { defaultValue: "Folders" })}</span>
              </button>
            )}
            <button className="rp-collapse-btn" onClick={() => setDbCollapsed(prev => !prev)} title={dbCollapsed ? t("common.expand") : t("common.collapse")}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ transform: dbCollapsed ? "rotate(-90deg)" : "none", transition: "transform 0.2s" }}><polyline points="6 9 12 15 18 9"/></svg>
            </button>
            <button className={`rp-collapse-btn${viewMode === "list" ? " active" : ""}`} title={t("results.listView", { defaultValue: "List view" })} onClick={() => setViewMode("list")}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></svg>
            </button>
            <button className={`rp-collapse-btn${viewMode === "grid" ? " active" : ""}`} title={t("results.gridView", { defaultValue: "Icon view" })} onClick={() => setViewMode("grid")}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/></svg>
            </button>
          </div>
        </div>

        {/* Results grouped by database, then by date */}
        {!dbCollapsed && perGroupDateGroups.map((dbGroup) => (
          <React.Fragment key={dbGroup.name}>
            {perGroupDateGroups.length > 1 && (
              <div className="rp-db-section-header">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><ellipse cx="12" cy="5" rx="9" ry="3"/><path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3"/><path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5"/></svg>
                <span className="rp-db-section-name">{dbGroup.name}</span>
                <span className="rp-db-section-count">&middot; {dbGroup.count} {t("results.documents") || "documents"}</span>
              </div>
            )}
            <div className={viewMode === "grid" ? "rp-grid-wrap" : "rp-table-wrap"}>
              {Array.from(dbGroup.dateMap.entries()).map(([dateLabel, items]) => {
                const groupKey = `${dbGroup.name}:${dateLabel}`;
                const isGroupCollapsed = collapsedGroups.has(groupKey);
                return (
                <React.Fragment key={groupKey}>
                  <div className="rp-date-group">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/></svg>
                    <span className="rp-date-label">{dateLabel}</span>
                    <span className="rp-date-count">&middot; {items.length} {t("results.documents") || "documents"}</span>
                    <button className="rp-date-chevron" onClick={() => toggleDateGroup(groupKey)} title={isGroupCollapsed ? t("common.expand") : t("common.collapse")}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ transform: isGroupCollapsed ? "rotate(-90deg)" : "none", transition: "transform 0.2s" }}><polyline points="6 9 12 15 18 9"/></svg>
                    </button>
                  </div>

                  {!isGroupCollapsed && viewMode === "list" && (
                  <table className="rp-table">
                    <thead>
                      <tr>
                        <th style={{ width: 36 }}></th>
                        <th style={{ width: browseMode ? "34%" : "45%" }}>NAME</th>
                        {browseMode && <th style={{ width: 160 }}>DATABASE</th>}
                        <th style={{ width: "25%" }}>TAGS</th>
                        <th style={{ width: 130 }}>DATE</th>
                        <th style={{ width: 60, textAlign: "right" }}>PAGES</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((row, idx) => {
                        const key = `${groupKey}-${idx}`;
                        const ext = getExt(row).toLowerCase();
                        const name = getDocName(row);
                        const date = getDocDate(row);
                        const pages = getPageCount(row);
                        const isSelected = selectedDoc === row || (selectedDoc && getDocKey(selectedDoc) === getDocKey(row));
                        const isChecked = checkedDocs.has(key);
                        return (
                          <tr
                            key={key}
                            className={isSelected ? "selected" : ""}
                            draggable
                            onDragStart={startResultDrag(row)}
                            onClick={() => selectDocument(row)}
                            onMouseEnter={e => onRowHover(row, e)}
                            onMouseLeave={onRowLeave}
                          >
                            <td>
                              <span
                                className={`rp-check${isChecked ? " checked" : ""}`}
                                onClick={e => { e.stopPropagation(); toggleCheck(key); }}
                              />
                            </td>
                            <td>
                              <div className="rp-name-cell">
                                <span className={`rp-file-badge ${ext || "default"}`}>.{ext || "DOC"}</span>
                                <span className="rp-name-text">{name}</span>
                                <button
                                  className={`rp-pin-btn${isPinnedForCompare(row) ? " pinned" : ""}`}
                                  title={isPinnedForCompare(row) ? t("compare.unpin") : t("compare.pinToCompare")}
                                  onClick={e => { e.stopPropagation(); if (isPinnedForCompare(row)) { unpinForCompare(row); } else { flyToCompareTray(e.currentTarget); pinForCompare(row); } }}
                                >
                                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M12 2l2.09 6.26L21 9.27l-5 4.87L17.18 21 12 17.27 6.82 21 8 14.14l-5-4.87 6.91-1.01z"/>
                                  </svg>
                                </button>
                                {(() => { const gc = govRowChips(row, folderMap, govEnabled); return gc ? <span style={{ display: "inline-flex", gap: 4, marginLeft: 6, flexWrap: "wrap" }}>{gc}</span> : null; })()}
                              </div>
                            </td>
                            {browseMode && <td style={{ color: "#6b7280", fontSize: 13, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} title={getDbName(row) || ""}>{getDbName(row) || "-"}</td>}
                            <td className="rp-tags-col">
                              {(() => {
                                const tk = docTagKey(row, fallbackDb);
                                const rowTags = tk ? docTagsMap.get(tk) : null;
                                if (!rowTags?.length) return null;
                                return (
                                  <div className="rp-tag-pills">
                                    {rowTags.map((tg) => (
                                      <span
                                        key={tg.tagId}
                                        className={`rp-tag-pill${tg.tagColor ? "" : " rp-tag-pill--default"}`}
                                        style={tg.tagColor ? { background: tg.tagColor + "18", color: tg.tagColor, border: `1px solid ${tg.tagColor}40` } : undefined}
                                      >
                                        {tg.tagName}
                                      </span>
                                    ))}
                                  </div>
                                );
                              })()}
                            </td>
                            <td className="rp-date-col">{date ? fmtDate(date) : "-"}</td>
                            <td className="rp-pages-col">{pages != null ? pages : "-"}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  )}

                  {!isGroupCollapsed && viewMode === "grid" && (
                  <div className="rp-icon-grid">
                    {items.map((row, idx) => {
                      const key = `${groupKey}-${idx}`;
                      const ext = getExt(row).toLowerCase();
                      const name = getDocName(row);
                      const pages = getPageCount(row);
                      const isSelected = selectedDoc === row || (selectedDoc && getDocKey(selectedDoc) === getDocKey(row));
                      const isChecked = checkedDocs.has(key);
                      const showCheck = checkedDocs.size > 0;
                      return (
                        <div
                          key={key}
                          className={`rp-icon-card${isChecked ? " checked" : ""}${isSelected && !isChecked ? " selected" : ""}`}
                          draggable
                          onDragStart={startResultDrag(row)}
                          onClick={e => handleGridClick(key, row, e)}
                          onMouseEnter={e => onRowHover(row, e)}
                          onMouseLeave={onRowLeave}
                          title={name}
                        >
                          <span
                            className={`rp-icon-check${isChecked ? " on" : ""}${showCheck ? " visible" : ""}`}
                            onClick={e => { e.stopPropagation(); toggleCheck(key); lastClickedIdx.current = allKeys.indexOf(key); }}
                          />
                          <button
                            className={`rp-pin-btn rp-pin-grid${isPinnedForCompare(row) ? " pinned" : ""}`}
                            title={isPinnedForCompare(row) ? t("compare.unpin") : t("compare.pinToCompare")}
                            onClick={e => { e.stopPropagation(); if (isPinnedForCompare(row)) { unpinForCompare(row); } else { flyToCompareTray(e.currentTarget); pinForCompare(row); } }}
                          >
                            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M12 2l2.09 6.26L21 9.27l-5 4.87L17.18 21 12 17.27 6.82 21 8 14.14l-5-4.87 6.91-1.01z"/>
                            </svg>
                          </button>
                          <div className="rp-icon-doc">
                            <svg width="32" height="40" viewBox="0 0 32 40" fill="none">
                              <path d="M2 0h20l10 10v28a2 2 0 01-2 2H2a2 2 0 01-2-2V2a2 2 0 012-2z" fill="#f3f4f6"/>
                              <path d="M22 0l10 10h-8a2 2 0 01-2-2V0z" fill="#d1d5db"/>
                            </svg>
                            <span className={`rp-icon-ext ${ext || "default"}`}>.{ext || "DOC"}</span>
                          </div>
                          <div className="rp-icon-name">{name}</div>
                          {(() => { const gc = govRowChips(row, folderMap, govEnabled); return gc ? <div style={{ display: "flex", justifyContent: "center", gap: 4, flexWrap: "wrap", marginTop: 2 }}>{gc}</div> : null; })()}
                          {(() => {
                            const tk = docTagKey(row, fallbackDb);
                            const rowTags = tk ? docTagsMap.get(tk) : null;
                            if (!rowTags?.length) return null;
                            return (
                              <div className="rp-tag-pills" style={{ justifyContent: "center", marginTop: 2 }}>
                                {rowTags.slice(0, 2).map((tg) => (
                                  <span
                                    key={tg.tagId}
                                    className={`rp-tag-pill${tg.tagColor ? "" : " rp-tag-pill--default"}`}
                                    style={tg.tagColor ? { background: tg.tagColor + "18", color: tg.tagColor, border: `1px solid ${tg.tagColor}40` } : undefined}
                                  >
                                    {tg.tagName}
                                  </span>
                                ))}
                                {rowTags.length > 2 && <span className="rp-tag-pill rp-tag-pill--default">+{rowTags.length - 2}</span>}
                              </div>
                            );
                          })()}
                          {pages != null && <div className="rp-icon-pages">{pages} {t("results.pages", { defaultValue: "pages" })}</div>}
                        </div>
                      );
                    })}
                  </div>
                  )}
                </React.Fragment>
                );
              })}
            </div>
          </React.Fragment>
        ))}

        {hoveredRow && (
          <div className="rp-thumb-popup" style={{ left: hoverPos.x, top: hoverPos.y }}>
            <div className="rp-thumb-img">
              {thumbState?.url ? (
                <img src={thumbState.url} alt="" style={{ width: "100%", height: "100%", objectFit: "contain" }} />
              ) : thumbState?.loading ? (
                <div className="rp-thumb-loading">
                  <span className="dp-spinner" />
                </div>
              ) : (
                <div className="rp-placeholder-doc">
                  <div className="rp-placeholder-line" style={{ width: "90%", background: "#aaa" }} />
                  <div className="rp-placeholder-line" style={{ width: "70%" }} />
                  <div className="rp-placeholder-line" style={{ width: "85%" }} />
                  <div className="rp-placeholder-line" style={{ width: "60%" }} />
                  <div className="rp-placeholder-line" style={{ width: "75%", marginTop: 12 }} />
                  <div className="rp-placeholder-line" style={{ width: "50%" }} />
                </div>
              )}
            </div>
            <div className="rp-thumb-meta">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
              {getPageCount(hoveredRow) || "?"} {t("results.pages", { defaultValue: "pages" })}
            </div>
          </div>
        )}
      </>
    );
  }

  // Folder-browse chrome (Explorer): breadcrumb + subfolder tiles above the listing. The document
  // listing is the normal results view (rich rows / list-grid toggle / hover preview) fed by the
  // folder filter. Only when licensed, idle (no typed query), and databases are available.
  // The idle landing is the home DASHBOARD, not an empty results table: what is waiting on this user, what
  // they started, their folders, what they last opened. Licensed on content-governance, exactly like the
  // folder Explorer it replaces — without that feature the pane keeps its previous empty state. The moment a
  // query is typed or a folder is opened this is gone and the ordinary results view has the pane back.
  const dashboardActive =
    !loading && !error && hasDbs && !wizardFlag && !hasResults
    && browseCwd == null && !(query && String(query).trim());

  if (dashboardActive) {
    // The idle landing is the three-layer Explorer. Whichever lens is chosen, the SAME archive is
    // being looked at from a different angle — so opening a document from any of them lands in the
    // ordinary detail pane, and no lens is a separate document population.
    //
    // Personal is the default and is deliberately NOT licence-gated: having somewhere to keep your own
    // documents is what makes the product usable on day one. The org lenses (and the summary strip above
    // them) stay behind content-governance, because a folder plan and a controlled vocabulary are the
    // governance tier's machinery.
    resultsContent = (
      <div style={{ padding: "8px 4px 40px" }}>
        {/* The landing summary heads the screen: the greeting and the counters as a strip, then the
            workspace directly beneath it. Only that strip — its lists stay in the detail panel, where
            they were; carrying them up here would push the workspace, which is what the centre is for,
            a screen down. */}
        {govEnabled && (
          <HomeDashboard variant="banner" folders={folderList} workflowEnabled={govEnabled} />
        )}

        <LayerSwitcher value={lens} onChange={setLens} showOrgLayers={govEnabled} />

        {lens === LAYERS.workspace && (
          <WorkspaceExplorer onOpenDocument={openWorkspaceDocument} />
        )}

        {lens === LAYERS.taxonomy && govEnabled && (
          <>
            {/* The company's own filing: the folder plan browses exactly as it did before, with the
                controlled vocabulary beside it as the second, multi-valued way in. */}
            {folderList.length > 0 && (
              <div style={{ marginBottom: 24 }}>
                <FolderBreadcrumb cwd={null} folders={folderList} onNav={goFolder} t={t} />
                <div className="ws-tiles">
                  {folderList
                    .filter((f) => (f.parentId ?? null) === null)
                    .map((f) => <FolderTile key={f.id} folder={f} onOpen={() => goFolder(f.id)} />)}
                </div>
              </div>
            )}
            <OrgLayerExplorer mode="taxonomy" dbs={dbs} onRunSearch={runLensSearch} />
          </>
        )}

        {lens === LAYERS.bases && govEnabled && (
          <OrgLayerExplorer mode="bases" dbs={dbs} onRunSearch={runLensSearch} />
        )}
      </div>
    );
  } else if (browseActive && !error && hasDbs && !wizardFlag) {
    const subfolders = folderList
      .filter((f) => (f.parentId ?? null) === browseCwd)
      .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    const prior = resultsContent; // rich results when the current folder has documents
    const emptyMsg = (
      <div style={{ textAlign: "center", padding: "40px 0", color: "#9ca3af", fontSize: 13 }}>
        {t("explorer.emptyFolder", { defaultValue: "This folder is empty." })}
      </div>
    );
    resultsContent = (
      <div style={{ padding: "8px 4px 40px" }}>
        <FolderBreadcrumb cwd={browseCwd} folders={folderList} onNav={goFolder} t={t} />
        {subfolders.length > 0 && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: browseCwd != null && hasResults ? 18 : 4 }}>
            {subfolders.map((f) => <FolderTile key={f.id} folder={f} onOpen={() => goFolder(f.id)} />)}
          </div>
        )}
        {browseCwd != null
          ? (hasResults ? prior : emptyMsg)
          : (subfolders.length === 0 ? emptyMsg : null)}
      </div>
    );
  }

  const activeFlowObj = centerFlow
    ? flows.find((f) => f.name === centerFlow)
    : null;

  return (
    <div className="center-panel" style={{ position: "relative" }}>
      {formatPicker && (
        <FormatPickerModal
          item={formatPicker.item}
          formats={formatPicker.formats}
          loading={formatPicker.loading}
          error={formatPicker.error}
          onSelect={viewDocumentWithFormat}
          onClose={closeFormatPicker}
        />
      )}

      <FlowDrawer
        centerFlow={centerFlow}
        activeFlowObj={activeFlowObj}
        token={token}
        onClose={closeFlowInCenter}
        onResults={setFlowResults}
        onClear={clearFlowResults}
        t={t}
      />

      <div
        className="results-section"
        style={{ marginTop: centerFlow ? 12 : 0 }}
      >
        {resultsContent}
      </div>

      {canDeliverSelection && (
        <DeliverModal
          open={deliverOpen}
          dbName={[...checkedDbNames][0]}
          documentIds={checkedRows.map((r) => r.id ?? r.Id).filter((x) => x != null && x !== "")}
          onClose={() => setDeliverOpen(false)}
        />
      )}
    </div>
  );
}
